import { useEffect, useState } from 'preact/hooks';
import {
  getAvailabilityBlocks,
  addAvailabilityBlock,
  removeAvailabilityBlock,
  type AvailabilityBlock,
  type BlockTipo,
  CACHE_KEYS,
} from '../lib/api';
import { getCached } from '../lib/cache';
import { Skeleton } from '../components/Skeleton';
import { todayIsoAst } from '../lib/dates';
import { haptic } from '../lib/telegram';

const TYPE_META: Record<BlockTipo, { label: string; icon: string; cls: string }> = {
  Vacaciones: { label: 'Vacaciones', icon: '✈️', cls: 'vacation' },
  Enfermedad: { label: 'Enfermedad', icon: '🏥', cls: 'sick' },
  Bloqueo_Personal: { label: 'Bloqueo personal', icon: '📌', cls: 'personal' },
};

const MONTH_ES_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

function fmtBlockDate(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return iso;
  return `${parseInt(m[3], 10)} ${MONTH_ES_SHORT[parseInt(m[2], 10) - 1]}`;
}
function fmtRange(b: AvailabilityBlock): string {
  if (b.from === b.to) return fmtBlockDate(b.from);
  return `${fmtBlockDate(b.from)} – ${fmtBlockDate(b.to)}`;
}

export function Disponibilidad() {
  const [blocks, setBlocks] = useState<AvailabilityBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);

  // Form state
  const [tipo, setTipo] = useState<BlockTipo>('Bloqueo_Personal');
  const today = todayIsoAst();
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [notas, setNotas] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    setError(null);
    const cached = getCached<{ blocks: AvailabilityBlock[] }>(CACHE_KEYS.blocks);
    if (cached) {
      setBlocks(cached.value.blocks || []);
      setLoading(false);
    } else {
      setLoading(true);
    }
    try {
      const r = await getAvailabilityBlocks();
      setBlocks(r.blocks || []);
    } catch (e) {
      if (!cached) setError((e as Error).message || 'No pudimos cargar tu disponibilidad');
    } finally {
      setLoading(false);
    }
  }

  async function handleAdd() {
    if (busy) return;
    if (from > to) { alert('La fecha "Hasta" no puede ser antes de "Desde"'); return; }
    setBusy(true);
    haptic('medium');
    try {
      await addAvailabilityBlock({ from, to, tipo, notas: notas.trim() || undefined });
      haptic('success');
      setShowForm(false);
      setNotas('');
      await load();
    } catch (e) {
      haptic('error');
      const msg = (e as Error).message;
      alert(msg === 'range_too_long' ? 'Rango muy largo (máx 90 días).' : `No pudimos guardar: ${msg}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(b: AvailabilityBlock) {
    if (busy) return;
    if (!confirm(`¿Quitar el bloqueo de ${fmtRange(b)}?`)) return;
    setBusy(true);
    haptic('medium');
    try {
      await removeAvailabilityBlock({ from: b.from, to: b.to, tipo: b.tipo });
      haptic('success');
      await load();
    } catch (e) {
      haptic('error');
      alert(`No pudimos quitar: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div class="screen">
        <header class="app-header"><h1>Mi disponibilidad</h1></header>
        <div class="card">
          {[0, 1, 2].map((i) => (
            <div key={i} class="ledger-row">
              <div class="ledger-info" style={{ flex: 1 }}>
                <Skeleton width="55%" height={14} style={{ marginBottom: 6 }} />
                <Skeleton width="35%" height={12} />
              </div>
              <Skeleton width={20} height={20} borderRadius={10} />
            </div>
          ))}
        </div>
        <Skeleton height={40} borderRadius={10} style={{ marginTop: 12 }} />
      </div>
    );
  }

  if (error) {
    return (
      <div class="error-screen">
        <h2>No pudimos cargar</h2>
        <p>{error}</p>
        <button class="btn primary" onClick={load}>Reintentar</button>
      </div>
    );
  }

  return (
    <div class="screen">
      <header class="app-header">
        <h1>Mi disponibilidad</h1>
        <div class="subtitle">Bloquea fechas en las que no puedes trabajar.</div>
      </header>

      <div class="section-header">Fechas bloqueadas</div>

      {blocks.length === 0 ? (
        <div class="empty">
          <div class="ic">📅</div>
          <p>No tienes fechas bloqueadas. Estás disponible para todos los eventos.</p>
        </div>
      ) : (
        blocks.map((b, i) => {
          const m = TYPE_META[b.tipo] || TYPE_META.Bloqueo_Personal;
          return (
            <div class={`block-card ${m.cls}`} key={`${b.from}-${b.to}-${b.tipo}-${i}`}>
              <div class="ic-tag">{m.icon}</div>
              <div class="block-info">
                <div class="block-title">{m.label}</div>
                <div class="block-dates">{fmtRange(b)}</div>
                {b.notas && <div class="block-reason">"{b.notas}"</div>}
              </div>
              <button class="block-remove" disabled={busy} onClick={() => handleRemove(b)} aria-label="Quitar bloqueo">✕</button>
            </div>
          );
        })
      )}

      {!showForm ? (
        <div style={{ padding: '0 12px' }}>
          <button class="btn primary" onClick={() => setShowForm(true)}>+ Bloquear fechas</button>
        </div>
      ) : (
        <div class="form-card">
          <div class="lbl-group">Tipo de bloqueo</div>
          <div class="chips">
            {(['Vacaciones', 'Enfermedad', 'Bloqueo_Personal'] as BlockTipo[]).map((t) => {
              const m = TYPE_META[t];
              return (
                <button
                  type="button"
                  class={`chip ${tipo === t ? 'selected' : ''}`}
                  onClick={() => { setTipo(t); haptic('light'); }}
                  key={t}
                >{m.icon} {m.label}</button>
              );
            })}
          </div>

          <div class="lbl-group">Desde</div>
          <input
            type="date"
            class="select"
            value={from}
            min={today}
            onInput={(e) => setFrom((e.currentTarget as HTMLInputElement).value)}
          />

          <div class="lbl-group">Hasta</div>
          <input
            type="date"
            class="select"
            value={to}
            min={from}
            onInput={(e) => setTo((e.currentTarget as HTMLInputElement).value)}
          />

          <div class="lbl-group">Motivo (opcional)</div>
          <textarea
            rows={2}
            placeholder="Solo para tu referencia"
            value={notas}
            onInput={(e) => setNotas((e.currentTarget as HTMLTextAreaElement).value)}
          />

          <div class="form-actions">
            <button class="btn ghost" disabled={busy} onClick={() => { setShowForm(false); setNotas(''); }}>
              Cancelar
            </button>
            <button class="btn primary" disabled={busy} onClick={handleAdd}>
              {busy ? 'Guardando...' : 'Bloquear estas fechas'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
